import axios, { CancelTokenSource } from 'axios';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption, mapToDropdownOptionArrayWithSep } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { StyledScrollableContainer } from 'components/layout/PraxFormScrollableContainer';
import PxGrid from 'components/layout/PxGrid';
import StyledActionButton from 'components/layout/StyledActionButton';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import DiscardOrderChangesActionButton
  from 'components/medicaments/orderadministration/DiscardOrderChangesActionButton';
import { FormApi, FormState, Mutator } from 'final-form';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Icon, Popup, Table } from 'semantic-ui-react';
import { searchSelectedMedicaments } from 'services/medicamentServices';
import {
  automaticOrder,
  deleteOrderById,
  editOrder,
  findOrderAdministrationById,
  saveOrder,
  searchOrderAdministration,
  sendOrder
} from 'services/orderAdministrationServices';
import { getAllSuppliers } from 'services/suppliersService';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { OrderAdministrationSearchBy } from 'ts-types/api.enums';
import {
  MedicamentDto,
  OrderAdministrationDto,
  OrderAdministrationSearchRequest,
  OrderItemDto,
  SupplierDto,
  UpsertOrderAdministrationDto
} from 'ts-types/api.types';
import { toIsoDateString } from 'utils/dateUtils';
import { noop } from 'utils/functionUtils';
import { composeValidators, mustBeNumber, mustBePositiveNumber, required } from 'utils/validatorUtils';

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

const CustomScrollableContainer = styled(StyledScrollableContainer)`
  height: 300px;
  max-height: 300px;
  padding-top: 1rem;
  padding-bottom: 1rem;

  .selected-order-row {
    background-color: #e6e6e6;
  }
`

interface Props extends MessageSourceProps {
  allMedicaments: Array<MedicamentDto>,
  order?: UpsertOrderAdministrationDto,
  orderId?: number,
  onSave: () => void,
  onCancel: () => void
}

interface State {
  medicamentData: Array<MedicamentDto>,
  suppliers: Array<SupplierDto>,
  supplierOptions: Array<DropdownOption>,
  supplierOrders: Array<OrderAdministrationDto>,
  supplierOrderItemMedicaments: Array<OrderItemDto>,
  medicamentsLoaded: boolean,
  ordersBySupplierLoaded: boolean,
  orderItemMedicamentsLoaded: boolean,
  selectedSupplierId?: number,
  selectedOrder?: OrderAdministrationDto,
  isOrderEdited: boolean,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class OrderAdministrationModal extends Component<Props, State> {

  form: FormApi | undefined;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      medicamentData: [],
      suppliers: [],
      supplierOptions: [],
      supplierOrders: [],
      supplierOrderItemMedicaments: [],
      medicamentsLoaded: false,
      ordersBySupplierLoaded: false,
      orderItemMedicamentsLoaded: false,
      selectedSupplierId: undefined,
      isOrderEdited: false,
      selectedOrder: undefined,
      cancelTokenSource
    }

    this.loadRequiredData(cancelTokenSource);
  }

  loadRequiredData = async (cancelTokenSource: CancelTokenSource) => {

    const {allMedicaments, order, orderId} = this.props;

    try {

      const suppliersResponse = await getAllSuppliers(cancelTokenSource);
      const supplierId = order ? order.supplierId : suppliersResponse[0].id

      const request: Partial<OrderAdministrationSearchRequest> = {
        fromOrderDate: undefined,
        toOrderDate: undefined,
        searchBy: OrderAdministrationSearchBy.SUPPLIER_AND_CREATED,
        supplierId: supplierId
      }

      const supplierOrdersResponse = await searchOrderAdministration(request, cancelTokenSource);

      const selectedOrder =  orderId ? supplierOrdersResponse.filter(o => o.id === orderId)[0] : undefined

      this.setState({
        medicamentData: allMedicaments,
        suppliers: suppliersResponse,
        supplierOptions: mapToDropdownOptionArrayWithSep(
            suppliersResponse, ["description"], "id"),
        supplierOrders: supplierOrdersResponse,
        supplierOrderItemMedicaments: order ? order.orderItems : [],
        selectedSupplierId: supplierId,
        selectedOrder: selectedOrder
      });

    } finally {
      this.setState({
        medicamentsLoaded: true,
        ordersBySupplierLoaded: true,
        orderItemMedicamentsLoaded: true
      });
    }

  }

  refreshData = (supplierId?: number) => {

    const {selectedSupplierId, cancelTokenSource} = this.state;

    this.setState({
      ordersBySupplierLoaded: false,
      orderItemMedicamentsLoaded: false
    })

    const request: Partial<OrderAdministrationSearchRequest> = {
      fromOrderDate: undefined,
      toOrderDate: undefined,
      searchBy: OrderAdministrationSearchBy.SUPPLIER_AND_CREATED,
      supplierId: supplierId ? supplierId : selectedSupplierId
    }

    searchOrderAdministration(request, cancelTokenSource)
        .then(response => {
          this.setState({
            selectedSupplierId: request.supplierId,
            supplierOrders: response,
            supplierOrderItemMedicaments: [],
            selectedOrder: undefined,
            isOrderEdited: false
          })
        })
        .catch((e: any) => this.handleError(e.response.data))
        .finally(() => this.setState({
          ordersBySupplierLoaded: true,
          orderItemMedicamentsLoaded: true
        }))
  };

  addMedicamentToOrderItems = (medicament?: MedicamentDto) => {

    const {supplierOrderItemMedicaments, selectedOrder} = this.state;

    if (medicament) {

      const duplicateMedicament = supplierOrderItemMedicaments.filter(m => m.medicamentId === medicament.id)[0];

      if (!duplicateMedicament) {

        const orderItemMedicament: Partial<OrderItemDto> = {
          id: undefined,
          medicamentId: medicament.id,
          quantity: medicament.quantity,
          supplierQty: 0,
          orderId: selectedOrder ? selectedOrder.id : undefined
        }

        this.setState({
          supplierOrderItemMedicaments: [...supplierOrderItemMedicaments, orderItemMedicament as OrderItemDto]
        });

        this.setState({
          isOrderEdited: true
        });
      }
    }
  }

  setSelectedOrder = (order?: OrderAdministrationDto) => {

    const {cancelTokenSource} = this.state;

    if (order) {

      this.setState({
        orderItemMedicamentsLoaded: false
      })

      findOrderAdministrationById(order.id, cancelTokenSource)
          .then(response => this.setState({
            selectedOrder: order,
            supplierOrderItemMedicaments: response.orderItems,
            isOrderEdited: false
          }))
          .catch((e: any) => this.handleError(e.response.data))
          .finally(() => this.setState({
            orderItemMedicamentsLoaded: true
          }))
    }
  }

  removeOrder = (orderId: number) => {

    const {cancelTokenSource} = this.state;

    if (orderId) {

      deleteOrderById(orderId, cancelTokenSource)
          .then(() => this.refreshData())
          .catch((e: any) => this.handleError(e.response.data))
    }
  }

  removeOrderItem = (orderItemId: number | undefined, index: number) => {

    const {selectedOrder, supplierOrderItemMedicaments} = this.state;

    if (selectedOrder) {

      const newSupplierOrderItemMedicaments = supplierOrderItemMedicaments.filter(
          (oi, oiIndex) => (orderItemId && oi.id !== orderItemId) || oiIndex !== index);

      this.setState({
        supplierOrderItemMedicaments: newSupplierOrderItemMedicaments,
        isOrderEdited: true
      });
    }
  }

  sendOrder = () => {

    const {selectedOrder, supplierOrderItemMedicaments, cancelTokenSource} = this.state;

    if (selectedOrder && supplierOrderItemMedicaments.length > 0) {

      sendOrder(selectedOrder.id, cancelTokenSource)
          .then(response => this.refreshData())
          .catch((e: any) => this.handleError(e.response.data))
    }
  }

  automaticOrder = () => {
    const {selectedSupplierId, cancelTokenSource} = this.state;

    if (selectedSupplierId) {
      automaticOrder(selectedSupplierId, cancelTokenSource)
          .then(response => this.refreshData())
          .catch((e: any) => this.handleError(e.response.data))
    }
  }

  addOrder = async (values: any) => {

    const {selectedSupplierId, selectedOrder, cancelTokenSource} = this.state;

    let saveValues: Partial<UpsertOrderAdministrationDto> = {...selectedOrder};

    saveValues.supplierId = selectedSupplierId;
    saveValues.orderDate = values.orderDate;
    saveValues.orderItems = values.orderItems;

    await saveOrder(saveValues, cancelTokenSource)
        .then(response => this.refreshData())
        .catch((e: any) => this.handleError(e.response.data))
  };

  handleSubmit = async (values: any) => {
    const {selectedOrder, cancelTokenSource} = this.state;

    if (selectedOrder) {

      const request: Partial<UpsertOrderAdministrationDto> = {
        supplierId: selectedOrder.supplierId,
        orderDate: selectedOrder.orderDate,
        orderStatus: selectedOrder.orderStatus,
        orderItems: values.orderItems
      }

      editOrder(selectedOrder.id, request, cancelTokenSource)
          .then(response => this.setState({
            isOrderEdited: false,
            supplierOrderItemMedicaments: values.orderItems
          }))
          .catch((e: any) => this.handleError(e.response.data))
    }
  }

  handleMedicamentChange = debounce((values: any): void => {

    const {allMedicaments} = this.props;
    const {cancelTokenSource} = this.state;

    if (values.description) {

      this.setState({
        medicamentsLoaded: false
      });

      searchSelectedMedicaments(values.description, cancelTokenSource)
          .then(response => {
            this.setState({
              medicamentData: response
            });
          })
          .catch((e: any) => this.handleError(e.response.data))
          .finally(() =>
              this.setState({
                medicamentsLoaded: true
              }));
    } else if (values.description === "") {
      this.setState({
        medicamentData: allMedicaments
      });
    }
  }, 300);

  handleChange = debounce(({values}: FormState): void => {

    const {selectedSupplierId, supplierOrderItemMedicaments} = this.state;

    if (values.supplierId && values.supplierId !== selectedSupplierId) {
      this.setState({
        selectedSupplierId: values.supplierId
      })
      this.refreshData(values.supplierId)
    }

    if (values.orderItems !== supplierOrderItemMedicaments) {
      this.setState({
        isOrderEdited: true
      });
    }
  }, 300);

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {errorMessage} = this.state;

    return (
        <FormContainer>
          {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
          }
          <PraxFormGrid>
            <Grid.Row>
              <Grid.Column width={8}>
                {this.renderMedicamentFinalForm()}
              </Grid.Column>
              <Grid.Column width={8}>
                {this.renderOrderFinalForm()}
              </Grid.Column>
            </Grid.Row>
          </PraxFormGrid>
        </FormContainer>
    );
  };

  renderMedicamentFinalForm = () => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{description: ""}}
            subscription={{values: true}}
            render={this.renderMedicamentsTable}
        />
    );
  }

  renderMedicamentsTable = ({values}: FormRenderProps): React.ReactNode => {

    const {getMessage} = this.props;
    const {medicamentsLoaded, selectedOrder, medicamentData} = this.state;

    const noOrderSelectedString = !selectedOrder ? getMessage("orderAdministration.noOrderSelected") : "";

    return (
        <React.Fragment>
          <PxGrid>
            <Grid.Row>
              <Grid.Column width={10}>
                <h3>{getMessage("orderAdministration.medicament.title")}</h3>
              </Grid.Column>
              {!selectedOrder &&
                  <Grid.Column width={6}>
                    <i>{noOrderSelectedString}</i>
                  </Grid.Column>
              }
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4}>
                <label>{getMessage("search")}</label>
              </Grid.Column>
              <Grid.Column width={12}>
                <Field
                    name="description"
                    component={Input}
                    fluid
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <StyledScrollableContainer>
                  {medicamentsLoaded ?
                      <VirtualizedTable
                          rowCount={medicamentData.length}
                          rowGetter={this.medicamentsRowGetter}
                          rowRenderer={this.medicamentsRowRenderer}
                          rowHeight={42}
                          columns={[
                            {
                              width: 100,
                              label: "#",
                              dataKey: "index"
                            },
                            {
                              width: 150,
                              label: getMessage("orderAdministration.medicament.code"),
                              dataKey: "code"
                            },
                            {
                              width: 400,
                              flexGrow: 1,
                              label: getMessage("orderAdministration.medicament.description"),
                              dataKey: "description"
                            }
                          ]}
                      /> : <LoaderComponent message={getMessage("medicament.loading")}/>
                  }
                </StyledScrollableContainer>
              </Grid.Column>
            </Grid.Row>
          </PxGrid>
          <FormSpy subscription={{values: true}} onChange={() => this.handleMedicamentChange(values)}/>
        </React.Fragment>)
  }

  medicamentsRowGetter = ({index}: any) => {
    const {medicamentData} = this.state;

    Object.assign(medicamentData[index], {index: index + 1});

    return medicamentData[index];
  };

  medicamentsRowRenderer = ({className, columns, index, key, style}: any) => {

    const {selectedOrder, medicamentData} = this.state;

    const a11yProps = {"aria-rowindex": index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => selectedOrder
                ? this.addMedicamentToOrderItems(medicamentData[index])
                : noop
            }
        >
          {columns}
        </div>
    );
  };

  setValue: Mutator = ([name, newValue], state, {changeValue}) => {
    changeValue(state, name, value => newValue);
  };

  renderOrderFinalForm = () => {

    const {supplierOrderItemMedicaments} = this.state;

    return (
        <FinalForm
            onSubmit={(values) => {
              this.handleSubmit(values)
            }}
            initialValues={{
              supplierId: this.state.selectedSupplierId,
              orderDate: toIsoDateString(moment()),
              orderItems: supplierOrderItemMedicaments
            }}
            mutators={{setValue: this.setValue}}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderOrdersSection}
        />
    );
  }

  renderOrdersSection = ({form, values, submitting, handleSubmit}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {
      supplierOptions,
      supplierOrders,
      supplierOrderItemMedicaments,
      selectedOrder,
      isOrderEdited,
      selectedSupplierId
    } = this.state;

    this.form = form;

    const disabledAddOrderButton = submitting || !selectedSupplierId && !values.orderDate
    const disabledOrderButton = submitting || !selectedOrder
        || (supplierOrderItemMedicaments.length <= 0 || isOrderEdited);
    const disabledAutomaticOrderButton = submitting || !selectedSupplierId;

    return (<React.Fragment>
      <PxGrid>
        <Grid.Row>
          <Grid.Column width={16}>
            <h3>{getMessage("orderAdministration.orders")}</h3>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={4} verticalAlign="middle">
            {getMessage("orderAdministration.supplier")}
          </Grid.Column>
          <Grid.Column width={10}>
            <Field
                name="supplierId"
                component={Select}
                options={supplierOptions}
                fluid
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={4} verticalAlign="middle">
            {getMessage("orderAdministration.orderDate")}
          </Grid.Column>
          <Grid.Column width={5}>
            <Field
                name="orderDate"
                component={DatePicker}
                validate={required}
                fluid
            />
          </Grid.Column>

          <Grid.Column width={5}>
            {selectedOrder && isOrderEdited
                ? <DiscardOrderChangesActionButton
                    popupMessage={getMessage("orderAdministration.discardChangesPopupMessage.onAdd")}
                    onConfirm={() => this.addOrder(values)}
                    position={"left center"}
                    trigger={
                      <Button
                          type="button"
                          compact
                          disabled={disabledAddOrderButton}
                      >
                        {getMessage("orderAdministration.addOrder")}
                      </Button>
                    }
                />
                : <Button
                    type="button"
                    compact
                    disabled={disabledAddOrderButton}
                    onClick={() => this.addOrder(values)}
                >
                  {getMessage("orderAdministration.addOrder")}
                </Button>
            }
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            {this.renderSupplierOrdersTable(supplierOrders)}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            {this.renderOrderSelectedMedicamentsTable(supplierOrderItemMedicaments, handleSubmit)}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16} verticalAlign="bottom">
            <FormButtonsRow>
              {selectedOrder && isOrderEdited
                  ? <DiscardOrderChangesActionButton
                      popupMessage={getMessage("orderAdministration.discardChangesPopupMessage.onOrder")}
                      onConfirm={this.sendOrder}
                      position={"top center"}
                      trigger={
                        <Button
                            compact
                            type="button"
                            primary
                            disabled={disabledOrderButton}
                        >
                          {getMessage("orderAdministration.order")}
                        </Button>
                      }
                  />
                  : <Button
                      compact
                      type="button"
                      primary
                      disabled={disabledOrderButton}
                      onClick={this.sendOrder}
                  >
                    {getMessage("orderAdministration.order")}
                  </Button>
              }
              {isOrderEdited
                  ? <DiscardOrderChangesActionButton
                      popupMessage={getMessage("orderAdministration.discardChangesPopupMessage.onAutomaticOrder")}
                      onConfirm={this.automaticOrder}
                      position={"top center"}
                      trigger={
                        <Button
                            compact
                            type="button"
                            secondary
                            disabled={disabledAutomaticOrderButton}
                        >
                          {getMessage("orderAdministration.automaticOrder")}
                        </Button>
                      }
                  />
                  : <Button
                      compact
                      type="button"
                      secondary
                      disabled={disabledAutomaticOrderButton}
                      onClick={this.automaticOrder}
                  >
                    {getMessage("orderAdministration.automaticOrder")}
                  </Button>

              }
              {isOrderEdited
                  ? <DiscardOrderChangesActionButton
                      popupMessage={getMessage("orderAdministration.discardChangesPopupMessage.onClose")}
                      onConfirm={onCancel}
                      position={"left center"}
                      trigger={
                        <Button
                            compact
                            type="button"
                            floated="right"
                            disabled={submitting}
                        >
                          {getMessage("button.close")}
                        </Button>
                      }
                  />
                  : <Button
                      compact
                      type="button"
                      floated="right"
                      disabled={submitting}
                      onClick={onCancel}
                  >
                    {getMessage("button.close")}
                  </Button>
              }
            </FormButtonsRow>
          </Grid.Column>
        </Grid.Row>
      </PxGrid>
      <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
    </React.Fragment>)
  }

  renderSupplierOrdersTable = (supplierOrders: Array<OrderAdministrationDto>): React.ReactNode => {

    const {getMessage} = this.props;
    const {suppliers, ordersBySupplierLoaded, selectedOrder} = this.state;

    return (
        <CustomScrollableContainer>
          {ordersBySupplierLoaded ?
              <Table celled selectable size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>{getMessage('Id')}</Table.HeaderCell>
                    <Table.HeaderCell>{getMessage('orderAdministration.orderDate')}</Table.HeaderCell>
                    <Table.HeaderCell>{getMessage('orderAdministration.supplier')}</Table.HeaderCell>
                    <Table.HeaderCell>{getMessage('orderAdministration.delete')}</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {
                    supplierOrders.map((order: OrderAdministrationDto, index: number) => {
                      return (
                          <Table.Row key={index}
                                     onClick={() => this.setSelectedOrder(order)}
                                     style={{cursor: "pointer"}}
                                     className={selectedOrder === order ? "selected-order-row" : ""}>
                            <Table.Cell width={2}>
                              {order.id}
                            </Table.Cell>
                            <Table.Cell width={6}>
                              {moment(order.orderDate).format("DD.MM.YYYY")}
                            </Table.Cell>
                            <Table.Cell width={6}>
                              {suppliers.filter(s => s.id === order.supplierId)[0].description}
                            </Table.Cell>
                            <Table.Cell width={2} textAlign="center">
                              {this.renderDeleteActionButton(() => this.removeOrder(order.id))}
                            </Table.Cell>
                          </Table.Row>
                      )
                    })
                  }
                </Table.Body>
              </Table>
              : <LoaderComponent message={getMessage("orderAdministration.orders.loading")}/>
          }
        </CustomScrollableContainer>
    )
  }

  renderOrderSelectedMedicamentsTable = (orderMeds: Array<Partial<OrderItemDto>>, handleSubmit: any): React.ReactNode => {

    const {getMessage, allMedicaments} = this.props;
    const {orderItemMedicamentsLoaded, isOrderEdited} = this.state;

    const orderMedicaments = orderMeds ? orderMeds : [];

    return (
        <CustomScrollableContainer>
          {orderItemMedicamentsLoaded ?
              <React.Fragment>
                <form onSubmit={handleSubmit}>

                  <Table celled selectable size="small">
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>{getMessage('orderAdministration.medicament')}</Table.HeaderCell>
                        <Table.HeaderCell>{getMessage('orderAdministration.quantity')}</Table.HeaderCell>
                        <Table.HeaderCell>{getMessage('orderAdministration.delete')}</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {
                        orderMedicaments.map((orderMed: Partial<OrderItemDto>, index: number) => {
                          const med = allMedicaments.filter(m => m.id === orderMed.medicamentId)
                          return (
                              <Table.Row key={index}>
                                <Table.Cell width={10}>
                                  {med[0].description}
                                </Table.Cell>
                                <Table.Cell width={4}>
                                  <Field
                                      style={{width: "70px"}}
                                      name={`orderItems[${index}].quantity`}
                                      component={Input}
                                      validate={composeValidators(mustBeNumber, mustBePositiveNumber, required)}
                                  />
                                </Table.Cell>
                                <Table.Cell width={2} textAlign="center">
                                  {this.renderDeleteActionButton(() => this.removeOrderItem(orderMed.id, index))}
                                </Table.Cell>
                              </Table.Row>
                          )
                        })
                      }
                    </Table.Body>
                  </Table>
                  {isOrderEdited &&
                      <Button
                          compact
                          type="submit"
                          color="grey"
                      >
                        {getMessage("button.save")}
                      </Button>
                  }
                </form>
              </React.Fragment>
              : <LoaderComponent message={getMessage("orderAdministration.orders.loading")}/>
          }
        </CustomScrollableContainer>
    )
  }

  renderDeleteActionButton = (deleteFunc: () => void): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <StyledActionButton icon
                            type="button"
        >
          <Popup
              trigger={<Icon name="trash"
                             onClick={() => deleteFunc()}
              />
              }
              content={getMessage("msgDelete")}
              size="small"
              position="top center"
              style={PopupStyle}
              inverted
          />
        </StyledActionButton>
    );
  }
}

export default withMessages(OrderAdministrationModal);
